import { Toast } from '@byecode/ui'
import type { ActionFlowNode, FlowNodeResultADT } from '@lighthouse/shared'
import { findNodeDom } from '@lighthouse/shared'
import { isIosPlatform } from '@lighthouse/tools'
import { snapdom } from '@zumer/snapdom'
import { t } from 'i18next'

import type { ExecuteActionParams } from '../type'

type ScreenShotConfig = { screenShotElementId?: string; scale?: number }

export const handleScreenShotStream = (
    node: ActionFlowNode<{ config: ScreenShotConfig }>,
    _runtimeParams: ExecuteActionParams
): Promise<FlowNodeResultADT> => {
    return screenShotCore(node.data.config, _runtimeParams.scope)
}

const screenShotCore = async (config: ScreenShotConfig | undefined, scope?: string): Promise<FlowNodeResultADT> => {
    const { screenShotElementId = '', scale } = config || {}
    try {
        Toast.info(t('generatingImage'), { icon: 'Loading', spinning: true, iconColor: '#CCCCCC', autoClose: false })
        const el = findNodeDom({ id: screenShotElementId, scope })
        if (!el) {
            Toast.error(t('componentNotFound'))
            return {
                type: 'interrupted',
                success: true
            }
        }

        // 清晰度倍率：内部高分辨率渲染，输出原尺寸
        // const renderScale = isIosPlatform() ? Math.min(Number(scale), 2) : Number(scale)
        const renderScale = Number(scale)
        const qualityMultiplier = 1 // 渲染精度倍数

        const snapdomOptions = {
            scale: renderScale * qualityMultiplier, // 高分辨率渲染
            dpr: qualityMultiplier, // 提高光栅化精度
            embedFonts: true
        }

        // 1. 获取高分辨率 canvas
        const highResCanvas = await snapdom.toCanvas(el, snapdomOptions)

        // 2. 计算目标输出尺寸（用户期望的 scale 倍率）
        const targetWidth = Math.round(el.clientWidth * renderScale)
        const targetHeight = Math.round(el.clientHeight * renderScale)

        // 3. 多步缩放算法：每次缩小一半，减少信息损失
        let currentCanvas = highResCanvas
        while (currentCanvas.width > targetWidth * 2) {
            const halfCanvas = document.createElement('canvas')
            halfCanvas.width = Math.round(currentCanvas.width / 2)
            halfCanvas.height = Math.round(currentCanvas.height / 2)
            const halfCtx = halfCanvas.getContext('2d')!
            halfCtx.imageSmoothingEnabled = true
            halfCtx.imageSmoothingQuality = 'high'
            halfCtx.drawImage(currentCanvas, 0, 0, halfCanvas.width, halfCanvas.height)
            currentCanvas = halfCanvas
        }

        // 4. 最终缩放到目标尺寸
        const outputCanvas = document.createElement('canvas')
        outputCanvas.width = targetWidth
        outputCanvas.height = targetHeight
        const ctx = outputCanvas.getContext('2d')!
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        ctx.drawImage(currentCanvas, 0, 0, targetWidth, targetHeight)

        // 5. 触发下载
        outputCanvas.toBlob(blob => {
            if (blob) {
                Toast.success(t('imageGeneratedSuccessfully'))
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = 'screenshot.png'
                a.click()
                URL.revokeObjectURL(url)
            }
        }, 'image/png')

        return {
            type: 'record',
            success: true
        }
    } catch (error) {
        console.error('截图失败:', error)
        Toast.close()
        Toast.error(t('imageGenerationFailed') || '生成图片失败')
        return {
            type: 'interrupted',
            success: true
        }
    }
}

export const handleScreenShotList = (
    config: ScreenShotConfig | undefined,
    _runtimeParams: ExecuteActionParams
): Promise<FlowNodeResultADT> => {
    const { scope } = _runtimeParams
    return screenShotCore(config, scope)
}
